import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as Parse from 'parse';
import * as Sentry from '@sentry/react';
import { AppThunk } from 'state';

interface State {
  hasPassword: boolean;
  unlocked: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: State = {
  hasPassword: true,
  unlocked: false,
  loading: false,
  error: null,
};

const passwordSlice = createSlice({
  name: 'password',
  initialState,
  reducers: {
    lock(state) {
      state.unlocked = false;
    },
    unlockStart(state) {
      state.loading = true;
      state.unlocked = false;
      state.error = null;
    },
    unlockSuccess(state) {
      state.loading = false;
      state.unlocked = true;
    },
    unlockFailed(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    setHasPassword(state, action: PayloadAction<boolean>) {
      state.hasPassword = action.payload;
    },
  },
});

export const { lock, unlockStart, unlockSuccess, unlockFailed, setHasPassword } =
  passwordSlice.actions;

export default passwordSlice.reducer;

/**
 * Thunks
 */

export const unlock =
  (password: string): AppThunk =>
    async (dispatch) => {
      try {
        dispatch(unlockStart());

        const currentPage = window.location.pathname;
        const currentUser = Parse.User.current()!;
        const restaurant = await currentUser.get('restaurant').fetch();
        const pagePassword =
          currentPage === '/menu' && restaurant.get('menuPassword') !== undefined
            ? restaurant.get('menuPassword')
            : restaurant.get('password');
        if (pagePassword === password) {
          dispatch(unlockSuccess());
        } else {
          dispatch(unlockFailed('Wrong password'));
        }
      } catch (error) {
        dispatch(unlockFailed(error.toString()));
        Sentry.captureException(error);
        // eslint-disable-next-line no-console
        console.error('unlock error', error);
      }
    };
export const checkHasPassword = (): AppThunk => async (dispatch) => {
  try {
    const currentUser = Parse.User.current()!;
    let restaurant: Parse.Object<Parse.Attributes> | undefined;

    // Check if user has a restaurant
    let userRestaurant = currentUser.get('restaurant');
    if (userRestaurant) {
      restaurant = await userRestaurant.fetch();
    } else {
      // If user doesn't have a restaurant, check for entity
      const entity = currentUser.get('entity');
      if (entity) {
        const entityDetails = await entity.fetch();
        const branches = entityDetails.get('branches');

        if (branches && branches.length > 0) {
          // Retrieve restaurant objectId from the first branch
          const branchRestaurant = branches[0];
          if (branchRestaurant.id) {
            // Search for this restaurant in all restaurants
            const restaurantQuery = new Parse.Query('Restaurant');
            restaurantQuery.equalTo('objectId', branchRestaurant.id);
            restaurant = await restaurantQuery.first(); // Get the first matching restaurant
          }
        }
      }
    }

    // Dispatch the result based on whether the restaurant has a password
    dispatch(setHasPassword(restaurant && restaurant.get('password')));

  } catch (e) {
    // Handle any errors and clear session if necessary
    window.localStorage.clear();
    dispatch(setHasPassword(false));
    Sentry.captureException(e);
    // eslint-disable-next-line no-console
    console.error('checkHasPassword error', e);
  }
};
